import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router';
import { Location } from 'history';
import withConfig from 'with-config';
import styled from 'styled-components';
import oh from 'output-helpers';

import SSO from 'sso-helper';

import LoginForm from '../components/LoginForm';
import FormCard from '../components/FormCard';
import NoSupportDialog from '../components/NoSupportDialog';
import backgroundImage from '../img/background.svg';

require('../modernizr');

const Container = styled.div`
    background: inherit;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url(${backgroundImage});
    background-position: center 0;
    background-repeat: no-repeat;
    background-color: #fff;
    overflow: auto;
`;

interface LoginProps {
    location: Location;
}

type SessionType = {
    service: string;
    return_url: string;
    service_title: string;
};

type Referrer = null | string;

const Login = ({ location }: LoginProps) => {
    let [processing, setProcessing] = useState(false);
    let [session, setSession] = useState({} as SessionType);
    let [error_message, setErrorMessage] = useState('');
    let [show_support_warning, setShowSupportWarning] = useState(false);

    useEffect(() => {
        const config = withConfig.getCurrentConfig();
        let query = new URLSearchParams(location.search);
        let referrer: Referrer = document.referrer;
        if (/.*login(\.[^.]+)?\.allbin.se/.test(referrer)) {
            referrer = null;
        }

        let service = query.get('service') || 'dashboard';
        let return_url =
            query.get('return_url') ||
            query.get('returnUrl') ||
            referrer ||
            config.dashboard.url ||
            'https://dashboard.allbin.se';

        if (window.sso) {
            window.sso = null;
        }

        const options: any = {};
        if (config.api.hasOwnProperty('sso')) {
            options.token_provider_uri = config.api.sso.api_uri;
            if (location.pathname.includes('/v2')) {
                options.token_provider_uri += '/v2';
            }
        }

        window.sso = new SSO(service, options);

        const service_title = service
            .split('_')
            .join(' ')
            .toUpperCase();
        setSession({ service, return_url, service_title });
    }, []);

    useEffect(() => {
        if (!window.Modernizr.promise_finally) {
            setShowSupportWarning(true);
        }
    }, []);

    if (show_support_warning) {
        return (
            <div>
                <NoSupportDialog
                    title={oh.translate('unsupported_browser_title')}
                    msg={oh.translate('unsupported_browser_msg')}
                    onClose={() => setShowSupportWarning(false)}
                />
            </div>
        );
    }

    return (
        <Container>
            <FormCard
                title={session.service_title}
                subtitle=""
                logo={session.service}
            >
                <LoginForm
                    disabled={processing}
                    error_message={error_message}
                    onCloseErrorMessage={() => setErrorMessage('')}
                    onSubmit={({ username, password }) => {
                        setProcessing(true);
                        window.sso
                            .loginWithCredentials(username.trim(), password)
                            .then((token: any) => {
                                window.location.href = `${
                                    session.return_url
                                }?token=${token.getRaw()}`;
                            })
                            .catch((err: any) => {
                                console.error(err);
                                let msg = '';
                                if (
                                    err.name &&
                                    err.name === 'InvalidTokenError'
                                ) {
                                    msg = oh.translate('error_invalid_token');
                                } else if (
                                    err.response &&
                                    err.response.status === 401
                                ) {
                                    msg = oh.translate(
                                        'error_invalid_credentials',
                                    );
                                } else if (
                                    err.response &&
                                    err.response.status >= 400 &&
                                    err.response.status < 500
                                ) {
                                    msg = oh.translate(
                                        'error_general_unauthorized',
                                    );
                                } else {
                                    msg = oh.translate('error_internal');
                                }
                                setErrorMessage(msg);
                            })
                            .finally(() => {
                                setProcessing(false);
                            });
                    }}
                />
            </FormCard>
        </Container>
    );
};

export default withRouter(Login);



// WEBPACK FOOTER //
// ./src/views/Login.tsx